import { DatePicker, Form, Input, InputNumber, Select, Switch } from "antd";
import { BankAccountGroupedLookup } from "../../shared/lookup/BankAccountGroupedLookup";
import { SmartLookupField } from "../../shared/lookup/SmartLookupField";
import { UI_DATE_FORMAT } from "../../shared/utils/date";

export type LookupLabels = Record<string, string | undefined>;

const monthOptions = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
].map((value) => ({ value, label: value }));

type EntryForm = ReturnType<typeof Form.useForm>[0];
type SetLabels = (updater: LookupLabels | ((current: LookupLabels) => LookupLabels)) => void;

export function DailyEntryFields({
  form,
  labels,
  setLabels,
  paymentType
}: {
  form: EntryForm;
  labels: LookupLabels;
  setLabels: SetLabels;
  paymentType?: string;
}) {
  return (
    <>
      <div className="two-column-form">
        <Form.Item name="paymentEntryDate" label="Entry Date" rules={[{ required: true }]}>
          <DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="paymentPaidDate" label="Paid Date">
          <DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="transactionType" label="Transaction Type" rules={[{ required: true }]}>
          <Select options={[{ value: "Debit" }, { value: "Credit" }]} />
        </Form.Item>
        <Form.Item name="paymentStatus" label="Payment Status" rules={[{ required: true }]}>
          <Select options={[{ value: "Pending" }, { value: "Paid" }, { value: "Failed" }, { value: "Cancelled" }]} />
        </Form.Item>
        <Form.Item name="paidOfId" label="Paid Of" rules={[{ required: true }]}>
          <SmartLookupField lookupKey="party" value={form.getFieldValue("paidOfId")} label={labels.paidOf} onChange={(v) => {
            form.setFieldValue("paidOfId", v.id);
            setLabels((current) => ({ ...current, paidOf: v.label }));
          }} />
        </Form.Item>
        <Form.Item name="paymentOfId" label="Payment Of" rules={[{ required: true }]}>
          <SmartLookupField lookupKey="party" value={form.getFieldValue("paymentOfId")} label={labels.paymentOf} onChange={(v) => {
            form.setFieldValue("paymentOfId", v.id);
            setLabels((current) => ({ ...current, paymentOf: v.label }));
          }} />
        </Form.Item>
        <Form.Item name="shortRemarkId" label="Short Remark" rules={[{ required: true }]}>
          <SmartLookupField lookupKey="shortRemark" value={form.getFieldValue("shortRemarkId")} label={labels.shortRemark} onChange={(v) => {
            form.setFieldValue("shortRemarkId", v.id);
            setLabels((current) => ({ ...current, shortRemark: v.label }));
          }} />
        </Form.Item>
        <Form.Item name="expenseMonthPayment" label="Expense Month" rules={[{ required: true }]}>
          <Select options={monthOptions} />
        </Form.Item>
        <Form.Item name="accountLedgerId" label="Entry Category" rules={[{ required: true }]}>
          <SmartLookupField lookupKey="entryCategory" value={form.getFieldValue("accountLedgerId")} label={labels.entryCategory} parentContext={{ transactionType: form.getFieldValue("transactionType") }} onChange={(v) => {
            form.setFieldValue("accountLedgerId", v.id);
            setLabels((current) => ({ ...current, entryCategory: v.label }));
          }} />
        </Form.Item>
        <Form.Item name="amount" label="Amount" rules={[{ required: true }, { type: "number", min: 1 }]}>
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="paymentType" label="Payment Type" rules={[{ required: true }]}>
          <Select options={[{ value: "Cash" }, { value: "Bank" }]} onChange={(value) => {
            if (value === "Cash") {
              form.setFieldsValue({ bankId: null, bankAccountId: null, paymentMode: "NA", paymentReference: null });
              setLabels((current) => ({ ...current, bank: undefined, bankAccount: undefined }));
            } else {
              form.setFieldValue("paymentMode", "UPI");
            }
          }} />
        </Form.Item>
        {paymentType === "Bank" ? (
          <>
            <Form.Item name="bankAccountId" label="Bank A/C" rules={[{ required: true }]}>
              <BankAccountGroupedLookup
                bankLabel={labels.bank}
                accountLabel={labels.bankAccount}
                onSelect={(value) => {
                  form.setFieldsValue({ bankId: value.bankId, bankAccountId: value.bankAccountId });
                  setLabels((current) => ({ ...current, bank: value.bankLabel, bankAccount: value.bankAccountLabel }));
                }}
              />
            </Form.Item>
            <Form.Item name="paymentMode" label="Payment Mode" rules={[{ required: true }]}>
              <Select options={[{ value: "Cheque" }, { value: "UPI" }, { value: "NEFT" }, { value: "RTGS" }, { value: "IMPS" }, { value: "Card" }, { value: "Wallet" }]} />
            </Form.Item>
            <Form.Item name="paymentReference" label="Reference No">
              <Input maxLength={100} />
            </Form.Item>
          </>
        ) : null}
      </div>
      <Form.Item name="longRemarks" label="Long Remark" rules={[{ required: true }]}>
        <Input.TextArea rows={4} />
      </Form.Item>
      <Form.Item name="bankId" hidden><Input /></Form.Item>
    </>
  );
}

export function CashEntryFields({
  form,
  labels,
  setLabels
}: {
  form: EntryForm;
  labels: LookupLabels;
  setLabels: SetLabels;
}) {
  return (
    <>
      <Form.Item name="dated" label="Entry Date" rules={[{ required: true }]}>
        <DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item name="paidOf" label="Paid Of" rules={[{ required: true }]}>
        <SmartLookupField lookupKey="party" value={form.getFieldValue("paidOf")} label={labels.paidOf ?? form.getFieldValue("paidOf")} onChange={(v) => {
          form.setFieldValue("paidOf", v.label);
          setLabels((current) => ({ ...current, paidOf: v.label }));
        }} />
      </Form.Item>
      <Form.Item name="paymentOf" label="Account (Cash/Bank)" rules={[{ required: true }]}>
        <Input maxLength={150} />
      </Form.Item>
      <div className="two-column-form">
        <Form.Item name="debit" label="Debit" rules={[{ type: "number", min: 0 }]}>
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="credit" label="Credit" rules={[{ type: "number", min: 0 }]}>
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      </div>
      <Form.Item shouldUpdate noStyle>
        {() => {
          const debit = Number(form.getFieldValue("debit") ?? 0);
          const credit = Number(form.getFieldValue("credit") ?? 0);
          return debit <= 0 && credit <= 0 ? (
            <div className="inline-error">Debit or Credit must be greater than zero.</div>
          ) : null;
        }}
      </Form.Item>
      <Form.Item name="remarks" label="Remarks">
        <Input.TextArea rows={3} maxLength={300} />
      </Form.Item>
    </>
  );
}

export function StatementEntryFields({
  form,
  labels,
  setLabels
}: {
  form: EntryForm;
  labels: LookupLabels;
  setLabels: SetLabels;
}) {
  return (
    <>
      <Form.Item name="tranDate" label="Transaction Date" rules={[{ required: true }]}>
        <DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item name="chequeNo" label="Account (Cash/Bank)">
        <SmartLookupField lookupKey="bankAccount" value={form.getFieldValue("chequeNo")} label={labels.chequeNo ?? form.getFieldValue("chequeNo")} parentContext={{ bankId: "" }} onChange={(v) => {
          form.setFieldValue("chequeNo", v.label);
          setLabels((current) => ({ ...current, chequeNo: v.label }));
        }} />
      </Form.Item>
      <Form.Item name="particulars" label="Particulars" rules={[{ required: true }]}>
        <SmartLookupField lookupKey="party" value={form.getFieldValue("particulars")} label={labels.particulars ?? form.getFieldValue("particulars")} onChange={(v) => {
          form.setFieldValue("particulars", v.label);
          setLabels((current) => ({ ...current, particulars: v.label }));
        }} />
      </Form.Item>
      <div className="two-column-form">
        <Form.Item name="debit" label="Debit" rules={[{ type: "number", min: 0 }]}>
          <InputNumber min={0} style={{ width: "100%" }} onChange={(value) => {
            if (Number(value ?? 0) > 0) form.setFieldsValue({ credit: 0, type: "Debit" });
          }} />
        </Form.Item>
        <Form.Item name="credit" label="Credit" rules={[{ type: "number", min: 0 }]}>
          <InputNumber min={0} style={{ width: "100%" }} onChange={(value) => {
            if (Number(value ?? 0) > 0) form.setFieldsValue({ debit: 0, type: "Credit" });
          }} />
        </Form.Item>
      </div>
      <Form.Item name="type" label="Type">
        <Select options={[{ value: "Debit" }, { value: "Credit" }]} />
      </Form.Item>
      <Form.Item name="narration" label="Narration">
        <Input.TextArea rows={3} maxLength={300} />
      </Form.Item>
    </>
  );
}
